/*This file conatains the common Validations and Formatting functions
that are used by multiple sheets for participant data entires
Product specific formatting functions are in their respective 
validations and formatting files*/

import { recordHook } from '@flatfile/plugin-record-hook'
import { format, isValid, isBefore } from 'date-fns'
import {
  parseDateFromString,
  findCountry,
  findUSState,
  validateTaxId,
  formatAndValidateCurrency,
} from 'utils'
import * as yup from 'yup'
import { US_COUNTRY_VALUE } from 'config/locations'
import { invalidPostalCodes } from 'config/products'

const numericRegex = /[0-9]/g
const NARegex = /^NA$/
const specialCharacterRegex =
  /[\u2018\u2019\u201A\u201B\u201C\u201D\u201E\u201F\u2032\u2033\u0022\u0060\u00B4\u0027]/g

const longDashRegex = /[\u00A0\s][–][\u00A0\s]/g
const isNumeric = (value) => value.replace(/\D/g, '') === value
const isNa = (value) => {
  const isString = typeof value === 'string'
  const formattedValue = value.toUpperCase()
  const matchesNa = formattedValue === 'N/A'
  return isString && matchesNa
}

export const planEinFormattingListener = (listener, sheetSlug) => {
  listener.use(
    recordHook(sheetSlug, (record) => {
      const planEin = record.get('planEin')
      if (planEin) {
        const numericRegex = /[0-9]/g
        const sanitizedPlanEin = planEin.match(numericRegex)?.join('')

        if (sanitizedPlanEin) {
          record.set(
            'planEin',
            sanitizedPlanEin.replace(/(\d{2})(\d+)/, '$1-$2')
          )
        }
      }
      return record
    })
  )
}

export const planSponsorFormattingListener = (listener, sheetSlug) => {
  listener.use(
    recordHook(sheetSlug, (record) => {
      const planSponsor = record.get('planSponsor')
      if (planSponsor) {
        const sanitizedPlanSponsor = planSponsor
          .match(specialCharacterRegex)
          ?.join('')
        if (sanitizedPlanSponsor) {
          record.set(
            'planSponsor',
            planSponsor.replace(specialCharacterRegex, "'")
          )
        }
      }
      return record
    })
  )
}

export const planNameFormattingListener = (listener, sheetSlug) => {
  listener.use(
    recordHook(sheetSlug, (record) => {
      const planName = record.get('planName')
      if (planName) {
        const sanitizedPlanName = planName
          .match(specialCharacterRegex)
          ?.join('')
        const sanitizedInput = planName.match(longDashRegex)?.join('')
        if (sanitizedPlanName) {
          record.set('planName', planName.replace(specialCharacterRegex, "'"))
        }
        if (sanitizedInput) {
          record.set('planName', planName.replace(longDashRegex, '-'))
        }
      }
      return record
    })
  )
}
export const planIdNumberFormattingListener = (listener, sheetSlug) => {
  listener.use(
    recordHook(sheetSlug, (record) => {
      const planIdNumber = record.get('planIdNumber')
      if (planIdNumber) {
        record.set(
          'planIdNumber',
          planIdNumber.replaceAll(/[^a-zA-Z0-9-]/g, '')
        )
      }
      return record
    })
  )
}

export const planNumberFormattingListener = (listener, sheetSlug) => {
  listener.use(
    recordHook(sheetSlug, (record) => {
      const planNumber = record.get('planNumber')
      if (planNumber) {
        const sanitizedPlanNumber = planNumber.match(numericRegex)?.join('')
        if (sanitizedPlanNumber) {
          const sanitizedInput = sanitizedPlanNumber.replace(/(\d{3})/, '$1')
          record.set('planNumber', sanitizedInput)
        }
      }
      return record
    })
  )
}

export const zipCodeFormattingListener = (listener, sheetSlug) => {
  listener.use(
    recordHook(sheetSlug, (record) => {
      const zipCode = record.get('zipCode')
      if (zipCode) {
        const sanitizedInput = zipCode.replaceAll(/[^a-zA-Z0-9- ]/g, '')
        let sanitizedZipCode = sanitizedInput.match(numericRegex)?.join('')
        if (sanitizedZipCode) {
          record.set('zipCode', sanitizedZipCode)
          if (sanitizedZipCode.length === 4 || sanitizedZipCode.length === 8) {
            sanitizedZipCode = '0' + sanitizedZipCode
            record.set('zipCode', sanitizedZipCode)
          } else if (
            sanitizedZipCode.length === 3 ||
            sanitizedZipCode.length === 7
          ) {
            sanitizedZipCode = '00' + sanitizedZipCode
            record.set('zipCode', sanitizedZipCode)
          } else if (
            sanitizedZipCode.length === 2 ||
            sanitizedZipCode.length === 6
          ) {
            sanitizedZipCode = '000' + sanitizedZipCode
            record.set('zipCode', sanitizedZipCode)
          }
        }
        if (sanitizedZipCode.length > 5) {
          record.set(
            'zipCode',
            sanitizedZipCode.slice(0, 5) + '-' + sanitizedZipCode.slice(5)
          )
        }
      }
      return record
    })
  )
}
export const dobValidationListener = (listener, sheetSlug) => {
  listener.use(
    recordHook(sheetSlug, (record) => {
      const dob = record.get('dob')
      if (dob) {
        if (NARegex.test(dob) || dob.trim() === '') {
          record.addError('dob', 'Required.')
        } else {
          const dobDate = new Date(dob)
          if (isValid(dobDate)) {
            const currentDate = new Date()
            const maxDate = new Date(currentDate)
            maxDate.setFullYear(currentDate.getFullYear() - 100)
            const minDate = new Date(currentDate)
            minDate.setFullYear(currentDate.getFullYear() - 10)
            if (dobDate > minDate) {
              record.addError('dob', 'Must be over 10 years.')
            } else {
              if (isBefore(dobDate, maxDate)) {
                record.addError('dob', 'Must be under 100 years.')
              }
            }
          } else {
            const dateRe = new RegExp('^[0-9]{4}/[0-9]{2}/[0-9]{2}$')
            dateRe.test(dob)
              ? record.addError('dob', 'Must be a valid date')
              : record.addError('dob', 'Format must be yyyy/mm/dd')
          }
        }
      }
      return record
    })
  )
}

export const dobFormattingListener = (listener, sheetSlug) => {
  listener.use(
    recordHook(sheetSlug, (record) => {
      const dob = record.get('dob')
      if (dob) {
        const dobDate = parseDateFromString(dob.trim())
        let formattedDob = dob
        if (dobDate) {
          formattedDob = format(dobDate, 'yyyy/MM/dd')
          record.set('dob', formattedDob)
        } else {
          const sanitizedDob = dob.match(numericRegex)?.join('')
          if (sanitizedDob) {
            formattedDob = sanitizedDob.replace(
              /(\d{4})(\d{2})(\d{2})/,
              '$1/$2/$3'
            )
            record.set('dob', formattedDob)
          }
        }
      }
      return record
    })
  )
}

export const ssnFormattingListener = (listener, sheetSlug) => {
  listener.use(
    recordHook(sheetSlug, (record) => {
      const ssn = record.get('ssn')
      if (ssn) {
        const firstLetter = ssn.match(/(([\d]\D*?){9})([a-zA-Z])/)?.[3] || ''
        const sanitizedSsn = ssn.match(numericRegex)?.join('')
        if (sanitizedSsn) {
          const finalizedSsn =
            sanitizedSsn.replace(/(\d{3})(\d{2})(\d{4})/, '$1-$2-$3') +
            firstLetter
          record.set('ssn', finalizedSsn)
        }
      }
      return record
    })
  )
}

export const ssnValidationListener = (listener, sheetSlug) => {
  listener.use(
    recordHook(sheetSlug, (record) => {
      const ssn = record.get('ssn')
      if (ssn) {
        if (NARegex.test(ssn)) {
          record.addError('ssn', 'Required.')
        } else {
          const ssnRe = new RegExp('^[0-9]{3}-[0-9]{2}-[0-9]{4}[a-zA-Z]?$')
          if (!ssnRe.test(ssn)) {
            record.addError('ssn', 'Format must be XXX-XX-XXXX')
          } else if (!validateTaxId(ssn)) {
            record.addError('ssn', 'Given SSN is not valid')
          }
        }
      }
      return record
    })
  )
}

export const countryCodeFormattingListener = (listener, sheetSlug) => {
  listener.use(
    recordHook(sheetSlug, (record) => {
      const countryCode = record.get('countryCode')
      if (countryCode) {
        const matchingCountry = findCountry(countryCode)
        if (matchingCountry) {
          record.set('countryCode', matchingCountry.value)
        } else {
          record.addError(
            'countryCode',
            'Must be a valid country name or country code'
          )
        }
      }
      return record
    })
  )
}

export const stateFormattingListener = (listener, sheetSlug) => {
  listener.use(
    recordHook(sheetSlug, (record) => {
      const state = record.get('state')
      const countryCode = record.get('countryCode')
      if (state && countryCode === US_COUNTRY_VALUE) {
        const matchingState = findUSState(state)
        if (matchingState) {
          record.set('state', matchingState.value)
        } else {
          record.addError('state', 'Must be a valid US state')
        }
      }
      return record
    })
  )
}

export const emailAddressValidationListener = (listener, sheetSlug) => {
  listener.use(
    recordHook(sheetSlug, (record) => {
      const emailAddress = record.get('emailAddress')
      if (emailAddress) {
        const schema = yup.string().email()
        const isValidEmail = schema.isValidSync(emailAddress)
        if (!isValidEmail) {
          record.addError(
            'emailAddress',
            'Must be valid email format: example@email.com'
          )
        }
      }
      return record
    })
  )
}

export const countryCodeStateCityZipValidationListener = (
  listener,
  sheetSlug
) => {
  listener.use(
    recordHook(sheetSlug, (record) => {
      const countryCode = record.get('countryCode')
      const state = record.get('state')
      const city = record.get('city')
      const zipCode = record.get('zipCode')
      if (countryCode === US_COUNTRY_VALUE) {
        if (zipCode) {
          const zipCodeRe = new RegExp('^[0-9]{5}(?:-[0-9]{4})?$')
          const postalCode = zipCode.substring(0, 5)
          if (!zipCodeRe.test(zipCode)) {
            record.addError(
              'zipCode',
              'US Zip Codes must have format: XXXXX or XXXXX-XXXX'
            )
          } else if (invalidPostalCodes.includes(postalCode)) {
            record.addError('zipCode', 'Please enter a valid Zip Code')
          }
        } else {
          // city and state are required if zip code is missing
          if (!city) record.addError('city', 'Required.')
          if (!state) record.addError('state', 'Required.')
          // zip code is required if both city and state are missing
          if (!city && !state) record.addError('zipCode', 'Required.')
        }
      } else {
        if (state && state.length > 22) {
          record.addError(
            'state',
            'Maximum length exceeded - must be 22 characters or less'
          )
        }
      }
      return record
    })
  )
}

export const traditionalIraPreTaxAmountFormattingListener = (
  listener,
  sheetSlug
) => {
  listener.use(
    recordHook(sheetSlug, (record) => {
      const traditionalIraPreTaxAmount = record.get(
        'traditionalIraPreTaxAmount'
      )
      if (traditionalIraPreTaxAmount) {
        formatAndValidateCurrency(
          traditionalIraPreTaxAmount,
          'traditionalIraPreTaxAmount',
          record
        )
      }
      return record
    })
  )
}

export const traditionalIraAfterTaxAmountFormattingListener = (
  listener,
  sheetSlug
) => {
  listener.use(
    recordHook(sheetSlug, (record) => {
      const traditionalIraAfterTaxAmount = record.get(
        'traditionalIraAfterTaxAmount'
      )
      if (traditionalIraAfterTaxAmount) {
        formatAndValidateCurrency(
          traditionalIraAfterTaxAmount,
          'traditionalIraAfterTaxAmount',
          record
        )
      }
      return record
    })
  )
}

export const rothIraAmountFormattingListener = (listener, sheetSlug) => {
  listener.use(
    recordHook(sheetSlug, (record) => {
      const rothIraAmount = record.get('rothIraAmount')
      if (rothIraAmount) {
        formatAndValidateCurrency(rothIraAmount, 'rothIraAmount', record)
      }
      return record
    })
  )
}
export const decedentDobValidationListener = (listener, sheetSlug) => {
  listener.use(
    recordHook(sheetSlug, (record) => {
      const dob = record.get('decedentDateOfBirth')
      if (dob) {
        if (NARegex.test(dob) || dob.trim() === '') {
          record.addError('decedentDateOfBirth', 'Required.')
        } else {
          const dobDate = new Date(dob)
          if (isValid(dobDate)) {
            const currentDate = new Date()
            const maxDate = new Date(currentDate)
            maxDate.setFullYear(currentDate.getFullYear() - 100)
            const minDate = new Date(currentDate)
            minDate.setFullYear(currentDate.getFullYear() - 10)
            if (dobDate > minDate) {
              record.addError('decedentDateOfBirth', 'Must be over 10 years.')
            } else {
              if (isBefore(dobDate, maxDate)) {
                record.addError(
                  'decedentDateOfBirth',
                  'Must be under 100 years.'
                )
              }
            }
          } else {
            const dateRe = new RegExp('^[0-9]{4}/[0-9]{2}/[0-9]{2}$')
            dateRe.test(dob)
              ? record.addError('decedentDateOfBirth', 'Must be a valid date')
              : record.addError(
                  'decedentDateOfBirth',
                  'Format must be yyyy/mm/dd'
                )
          }
        }
      }
      return record
    })
  )
}

export const decedentDobFormattingListener = (listener, sheetSlug) => {
  listener.use(
    recordHook(sheetSlug, (record) => {
      const dob = record.get('decedentDateOfBirth')
      if (dob) {
        const dobDate = parseDateFromString(dob.trim())
        let formattedDob = dob
        if (dobDate) {
          formattedDob = format(dobDate, 'yyyy/MM/dd')
          record.set('decedentDateOfBirth', formattedDob)
        } else {
          const sanitizedDob = dob.match(numericRegex)?.join('')
          if (sanitizedDob) {
            formattedDob = sanitizedDob.replace(
              /(\d{4})(\d{2})(\d{2})/,
              '$1/$2/$3'
            )
            record.set('decedentDateOfBirth', formattedDob)
          }
        }
      }
      return record
    })
  )
}

export const decedentDodValidationListener = (listener, sheetSlug) => {
  listener.use(
    recordHook(sheetSlug, (record) => {
      const dod = record.get('decedentDateOfDeath')
      if (dod) {
        const dodDate = isNumeric(dod) ? null : new Date(dod)

        if (isValid(dodDate)) {
          if (dodDate > new Date()) {
            record.addError('decedentDateOfDeath', 'Cannot be in the future')
          } else {
            if (isBefore(dodDate, new Date(0, 0, 1))) {
              record.addError(
                'decedentDateOfDeath',
                'Must be more recent than 1900'
              )
            }
          }
        } else {
          if (!isNa(dod)) {
            const dateRe = new RegExp('^[0-9]{1,2}/[0-9]{1,2}/[0-9]{4}$')
            dateRe.test(dod)
              ? record.addError('decedentDateOfDeath', 'Must be a valid date')
              : record.addError(
                  'decedentDateOfDeath',
                  'Format must be single-digit M if applicable, otherwise MM/DD/YYYY'
                )
          }
        }
      }
      return record
    })
  )
}
export const decedentDodFormattingListener = (listener, sheetSlug) => {
  listener.use(
    recordHook(sheetSlug, (record) => {
      const dod = record.get('decedentDateOfDeath')
      if (dod) {
        const dodDate = isNumeric(dod) ? null : parseDateFromString(dod)
        let formattedDod = dod

        if (dodDate) {
          formattedDod = format(dodDate, 'M/d/yyyy')
          record.set('decedentDateOfDeath', formattedDod)
        } else {
          const sanitizedDod = dod.match(/[0-9\\/]/g)?.join('')
          if (sanitizedDod && sanitizedDod !== '/') {
            formattedDod = sanitizedDod
            record.set('decedentDateOfDeath', formattedDod)
          } else {
            formattedDod = isNa(dod) ? dod.toUpperCase() : dod
            record.set('decedentDateOfDeath', formattedDod)
          }
        }
      }
      return record
    })
  )
}
